import React, { FC, ReactNode } from 'react';
import { create } from 'zustand';
import { ApiService } from '~/data/restApi';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';

interface AIModificationStatus {
  isLoading: boolean;
  error: string | null;
  success: boolean;
  lastPrompt: string;
}

interface SequenceMetadata {
  canUndo: boolean;
  canRedo: boolean;
}

interface ResponseWithMetadata {
  sequenceMetadata?: SequenceMetadata;
  sequence?: unknown;
  subitem?: unknown;
  error?: boolean;
  message?: string;
}

interface Store {
  apiService: ApiService | null;
  setApiService: (token: string, companyId: number | string) => void;
  training: any;
  sequence: any;
  lastChapterId: number | null;
  contentEditable: boolean;
  aiModification: AIModificationStatus;
  featureFlags: string[];
  setFeatureFlags: (flags: string[]) => void;
  hasFeatureFlag: (flagName: string) => boolean;
  canUndo: boolean;
  canRedo: boolean;
  setUndoRedoState: (canUndo: boolean, canRedo: boolean) => void;
  undoAction: (
    sequenceId: number,
    callback?: (sequenceId: number, training: any) => void
  ) => Promise<void>;
  redoAction: (
    sequenceId: number,
    callback?: (sequenceId: number, training: any) => void
  ) => Promise<void>;
  editSequence(data: any): void;
  updateSequence(
    elementId: number | string,
    fieldType: string,
    value: any,
    type?: string
  ): void;
  setContentEditable(data: boolean): void;
  setTraining(data: any): void;
  setSequence(data: any): void;
  nextSequence(): void;
  prevSequence(): void;
  collectIds(obj: any): string;
  addSubItem(data: any): void;
  removeSubItem(subItemId: number | string): void;
  updateSubItem(
    subItemId: number | string,
    fieldType: string,
    value: any
  ): Promise<{ error?: boolean; message?: string }>;
  updateFile(id: string, data: any, type: string, parentId?: string): void;
  setNarration(audio?: any): void;
  updateChapter(chapterId: number, data: any): void;
  createNewChapter(chapterName: string): void;
  resetAIModificationStatus: () => void;
  nextTooltip: () => void;
  setAIModificationStatus: (status: Partial<AIModificationStatus>) => void;
  modifySequenceWithAI(instructions: string): Promise<{
    success?: boolean;
    error?: boolean;
    message?: string;
    sequence?: any;
  }>;
  showTooltips: boolean;
  setShowTooltips: (showTooltips: boolean) => void;
  tooltip: number;
  prevTooltip: () => void;
  history: any[];
  undo: (cb: (sequenceId: number, training: any) => void) => void;
  redo: (cb: (sequenceId: number, training: any) => void) => void;
  clearHistory: () => void;
  forceUpdateSequence: boolean;
  setForceUpdateSequence: (forceUpdateSequence: boolean) => void;
  removeHistoryForSequence: (sequenceId: number) => void;
  updateTrainingTitle: (title: string) => void;
}

const HISTORY_STORAGE_KEY = 'easyllame_editor_history';

const saveHistoryToStorage = (history: any[]) => {
  try {
    sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    console.warn('Failed to save history to sessionStorage:', error);
  }
};

const loadHistoryFromStorage = (): any[] => {
  try {
    const stored = sessionStorage.getItem(HISTORY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn('Failed to load history from sessionStorage:', error);
    return [];
  }
};

const extractAndStoreMetadata = (
  response: ResponseWithMetadata,
  state: Pick<Store, 'hasFeatureFlag' | 'setUndoRedoState'>
) => {
  if (
    response?.sequenceMetadata &&
    state.hasFeatureFlag('course_editor_backend_undo_redo')
  ) {
    state.setUndoRedoState(
      response.sequenceMetadata.canUndo,
      response.sequenceMetadata.canRedo
    );
  }
};

export const useEditorStore = create<Store>((set, get) => ({
  apiService: null as ApiService | null,
  forceUpdateSequence: false,
  setForceUpdateSequence: (forceUpdateSequence: boolean) =>
    set({ forceUpdateSequence }),
  setApiService: (token: string, companyId: number | string) =>
    set({ apiService: new ApiService(token, companyId) }),
  featureFlags: [],
  setFeatureFlags: (flags: string[]) => set({ featureFlags: flags }),
  hasFeatureFlag: (flagName: string) => {
    return get().featureFlags.includes(flagName);
  },
  canUndo: false,
  canRedo: false,
  setUndoRedoState: (canUndo: boolean, canRedo: boolean) => {
    set({ canUndo, canRedo });
  },
  history: loadHistoryFromStorage(),

  clearHistory: () => {
    sessionStorage.removeItem(HISTORY_STORAGE_KEY);
    set({ history: [] });
  },

  undo: (cb: (sequenceId: number, training: any) => void) =>
    set(state => {
      // state.setForceUpdateSequence(false);

      const currentHistory = state.history.filter(
        item => item.sequenceId === state.sequence.id
      );

      if (currentHistory.length === 0) {
        return { ...state };
      }

      const currentActiveIndex = currentHistory.findIndex(
        item => item.active === true
      );

      state.history.forEach(item => {
        if (item.sequenceId === state.sequence.id) {
          item.active = false;
        }
      });

      if (currentActiveIndex > 0) {
        const previousHistory = currentHistory[currentActiveIndex - 1];
        previousHistory.active = true;

        const currentElement = currentHistory[currentActiveIndex];

        if (
          currentElement &&
          currentElement.elementId &&
          currentElement.fieldType &&
          currentElement.oldValue
        ) {
          if (currentElement.fieldType == 'pictures') {
            state.apiService
              ?.updateSubItem(
                currentElement.elementId,
                'picture_ids',
                currentElement.oldValue.map((file: any) => file.id)
              )
              .then(() => {
                cb(currentElement.sequenceId, state.training);
              });
          } else if (currentElement.fieldType == 'videos') {
            state.apiService
              ?.updateSubItem(
                currentElement.elementId,
                'video_ids',
                currentElement.oldValue.map((file: any) => file.id)
              )
              .then(() => {
                cb(currentElement.sequenceId, state.training);
              });
          } else if (currentElement.fieldType == 'documents') {
            state.apiService
              ?.updateSubItem(
                currentElement.elementId,
                'document_ids',
                currentElement.oldValue.map((file: any) => file.id)
              )
              .then(() => {
                cb(currentElement.sequenceId, state.training);
              });
          } else {
            const apiFieldType =
              currentElement.type === 'QUESTION_FIELD' &&
              currentElement.fieldType === 'value'
                ? 'title'
                : currentElement.fieldType;

            state.apiService
              ?.updateSubItem(
                currentElement.elementId,
                apiFieldType,
                currentElement.oldValue
              )
              .then(() => {
                cb(currentElement.sequenceId, state.training);
              });
          }
        }

        saveHistoryToStorage(state.history);

        return { ...state };
      }

      saveHistoryToStorage(state.history);
      return { ...state };
    }),

  redo: (cb: (sequenceId: number, training: any) => void) =>
    set(state => {
      // state.setForceUpdateSequence(false);

      const currentHistory = state.history.filter(
        item => item.sequenceId === state.sequence.id
      );

      if (currentHistory.length == 0) {
        return { ...state };
      }

      const currentActiveIndex = currentHistory.findIndex(
        item => item.active === true
      );

      state.history.forEach(item => {
        if (item.sequenceId === state.sequence.id) {
          item.active = false;
        }
      });

      if (
        currentActiveIndex >= 0 &&
        currentActiveIndex < currentHistory.length - 1
      ) {
        const nextHistory = currentHistory[currentActiveIndex + 1];
        nextHistory.active = true;

        if (
          nextHistory &&
          nextHistory.elementId &&
          nextHistory.fieldType &&
          nextHistory.value
        ) {
          if (nextHistory.fieldType == 'pictures') {
            state.apiService
              ?.updateSubItem(
                nextHistory.elementId,
                'picture_ids',
                nextHistory.value.map((file: any) => file.id)
              )
              .then(() => {
                cb(nextHistory.sequenceId, state.training);
              });
          } else if (nextHistory.fieldType == 'videos') {
            state.apiService
              ?.updateSubItem(
                nextHistory.elementId,
                'video_ids',
                nextHistory.value.map((file: any) => file.id)
              )
              .then(() => {
                cb(nextHistory.sequenceId, state.training);
              });
          } else if (nextHistory.fieldType == 'documents') {
            state.apiService
              ?.updateSubItem(
                nextHistory.elementId,
                'document_ids',
                nextHistory.value.map((file: any) => file.id)
              )
              .then(() => {
                cb(nextHistory.sequenceId, state.training);
              });
          } else {
            const apiFieldType =
              nextHistory.type === 'QUESTION_FIELD' &&
              nextHistory.fieldType === 'value'
                ? 'title'
                : nextHistory.fieldType;

            state.apiService
              ?.updateSubItem(
                nextHistory.elementId,
                apiFieldType,
                nextHistory.value
              )
              .then(() => {
                cb(nextHistory.sequenceId, state.training);
              });
          }
        }

        saveHistoryToStorage(state.history);

        return { ...state };
      }

      saveHistoryToStorage(state.history);

      return { ...state };
    }),
  training: [],
  sequence: null,
  sequences: [],
  lastChapterId: null,
  contentEditable: false,
  aiModification: {
    isLoading: false,
    error: null,
    success: false,
    lastPrompt: ''
  },
  showTooltips: false,
  setShowTooltips: (showTooltips: boolean) => set({ showTooltips }),
  tooltip: 0,
  nextTooltip: () => set(state => ({ tooltip: state.tooltip + 1 })),
  prevTooltip: () =>
    set(state => ({ tooltip: Math.max(0, state.tooltip - 1) })),
  collectIds: (obj: any) => {
    const ids: any[] = [];
    const traverse = (node: any) => {
      if (node && typeof node === 'object') {
        if (Object.prototype.hasOwnProperty.call(node, 'id')) {
          ids.push(node.id);
        }
        for (const key in node) {
          if (Object.prototype.hasOwnProperty.call(node, key)) {
            traverse(node[key]);
          }
        }
      }
    };

    traverse(obj);
    return ids.join('-');
  },
  editSequence: sequence =>
    set(state => {
      return { ...state, sequence: sequence };
    }),
  nextSequence: () =>
    set(state => {
      let nextSequence = state.sequence;
      let currentSequenceFind = false;

      state.training.chapters.forEach((chapter: any) => {
        chapter.chapterSequences.forEach((seq: any, index: number) => {
          if (currentSequenceFind) {
            currentSequenceFind = false;
            nextSequence = seq.sequence;
          }

          if (seq.sequence.id === state.sequence.id) {
            currentSequenceFind = true;
          }
        });
      });

      if (nextSequence.id) {
        state.apiService?.getSequenceInfo(nextSequence.id).then(sequence => {
          state.setSequence(sequence.sequence);
          extractAndStoreMetadata(sequence, state);
        });
      }
      return { ...state };
    }),
  prevSequence: () =>
    set(state => {
      let prevSequence = state.sequence;
      let stopSearch = false;

      state.training.chapters.forEach((chapter: any) => {
        chapter.chapterSequences.forEach((seq: any, index: number) => {
          if (seq.sequence.id != state.sequence.id && !stopSearch) {
            prevSequence = seq.sequence;
          }

          if (seq.sequence.id === state.sequence.id) {
            stopSearch = true;
          }
        });
      });

      if (prevSequence.id) {
        state.apiService?.getSequenceInfo(prevSequence.id).then(sequence => {
          state.setSequence(sequence.sequence);
          extractAndStoreMetadata(sequence, state);
        });
      }

      return { ...state };
    }),
  updateSequence: (elementId, fieldType, value, type) =>
    set(state => {
      if (!state.apiService) {
        console.error('ApiService is not initialized.');
        return state;
      }

      let _type = type;

      //we need to find the type of the element
      if (_type == undefined && fieldType == 'value') {
        const findItemById = (items: any[], id: string | number): any => {
          for (const item of items) {
            if (item.id == id) {
              return item;
            }
            if (item.items && item.items.length > 0) {
              const found = findItemById(item.items, id);
              if (found) {
                return found;
              }
            }
          }
          return null;
        };

        const foundItem = findItemById(state.sequence.items || [], elementId);
        _type = foundItem?.type;
      }

      let _fieldType = fieldType;

      // i know. I also love our backend code.
      // we need to use it, because backend team have many issues with files and they don't have 'value' field for buttons
      if (_type == 'QUESTION_FIELD' && fieldType == 'title') {
        _fieldType = 'value';
      }

      //i know, it is strange
      if (_type == 'QUESTION_FIELD' && fieldType == 'value') {
        state.apiService
          .updateSubItem(elementId, 'title', value)
          .then(response => {
            extractAndStoreMetadata(response, state);
          });
      } else {
        state.apiService
          .updateSubItem(elementId, fieldType, value)
          .then(response => {
            extractAndStoreMetadata(response, state);
          });
      }

      // update history
      const _history = state.history;

      let lastActiveIndex = -1;
      for (let i = _history.length - 1; i >= 0; i--) {
        if (
          _history[i].sequenceId === state.sequence.id &&
          _history[i].active === true
        ) {
          lastActiveIndex = i;
          break;
        }
      }

      if (lastActiveIndex >= 0) {
        _history.splice(lastActiveIndex + 1);
      }

      if (_history.length > 0) {
        const lastHistory = _history[_history.length - 1];
        if (
          lastHistory.sequenceId === state.sequence.id &&
          lastHistory.elementId == elementId &&
          lastHistory.fieldType == fieldType &&
          lastHistory.value == value &&
          lastHistory.type == type
        ) {
          return { ...state };
        }
      }

      // _history.forEach(item => {
      //   if (item.sequenceId == state.sequence.id) {
      //     item.active = false;
      //   }
      // });

      const updateItems = (items: any[]): any[] => {
        return items?.map(item => {
          if (item.id == elementId) {
            const existingHistoryItem = _history.find(
              historyItem =>
                historyItem.sequenceId === state.sequence.id &&
                historyItem.elementId === elementId &&
                historyItem.fieldType === fieldType &&
                historyItem.value === value
            );

            if (!existingHistoryItem) {
              if (
                !_history.find(
                  historyItem => historyItem.sequenceId == state.sequence.id
                )
              ) {
                _history.push({
                  sequenceId: state.sequence.id,
                  elementId: undefined,
                  fieldType: undefined,
                  value: undefined,
                  oldValue: undefined,
                  type: undefined,
                  active: false,
                  timestamp: Date.now()
                });
              }

              if (value != item[_fieldType]) {
                _history.forEach(historyItem => {
                  if (historyItem.sequenceId == state.sequence.id) {
                    historyItem.active = false;
                  }
                });

                _history.push({
                  sequenceId: state.sequence.id,
                  elementId,
                  fieldType,
                  value,
                  oldValue: item[_fieldType],
                  type,
                  active: true,
                  timestamp: Date.now()
                });
              }
            }

            return {
              ...item,
              [_fieldType]: value
            };
          }
          return item.items
            ? { ...item, items: updateItems(item.items) }
            : item;
        });
      };

      const updatedTraining = {
        ...state.training,
        chapters: state.training.chapters.map((chapter: any) => ({
          ...chapter,
          chapterSequences: chapter.chapterSequences.map((seq: any) => {
            return {
              ...seq,
              sequence: {
                ...seq.sequence,
                items: updateItems(seq.sequence.items)
              }
            };
          })
        }))
      };

      saveHistoryToStorage(_history);

      return {
        ...state,
        history: _history,
        sequence: {
          ...state.sequence,
          items: updateItems(state.sequence.items)
        },
        training: updatedTraining
      };
    }),
  setContentEditable: value =>
    set(state => ({ ...state, contentEditable: value })),
  setTraining: (data: any) => set(state => ({ ...state, training: data })),
  setSequence: (data: any) =>
    set(state => {
      return { ...state, sequence: data };
    }),

  undoAction: async (
    sequenceId: number,
    callback?: (sequenceId: number, training: any) => void
  ) => {
    const state = get();
    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return;
    }

    try {
      const response = await state.apiService.undoSequence(sequenceId);

      if (response.error) {
        console.error('Undo failed:', response.message);
        return;
      }

      extractAndStoreMetadata(response, state);

      if (callback) {
        callback(sequenceId, state.training);
      }
    } catch (error) {
      console.error('Undo failed:', error);
      throw error;
    }
  },

  redoAction: async (
    sequenceId: number,
    callback?: (sequenceId: number, training: any) => void
  ) => {
    const state = get();
    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return;
    }

    try {
      const response = await state.apiService.redoSequence(sequenceId);

      if (response.error) {
        console.error('Redo failed:', response.message);
        return;
      }

      extractAndStoreMetadata(response, state);

      if (callback) {
        callback(sequenceId, state.training);
      }
    } catch (error) {
      console.error('Redo failed:', error);
      throw error;
    }
  },

  addSubItem: (data: any) =>
    set(state => {
      if (!state.apiService) {
        console.error('ApiService is not initialized.');
        return state;
      }

      function insertAndReorder(items: any[], newItem: any) {
        items.sort((a, b) => a.position - b.position);

        const result = [];
        let inserted = false;

        for (const item of items) {
          if (!inserted && item.position >= newItem.position) {
            result.push({ ...newItem });
            inserted = true;
          }

          const newPosition = inserted ? item.position + 1 : item.position;
          result.push({ ...item, position: newPosition });
        }

        if (!inserted) {
          result.push({ ...newItem });
        }

        return result;
      }

      const api = state.apiService;

      api
        .addSubItem(data.sequenceId, data.data)
        .then(async newItem => {
          extractAndStoreMetadata(newItem, state);

          // optional file persist before updating state
          if (data.persistFile && data.imageFile && data.imageFile.id && api) {
            try {
              await api.updateSubItem(newItem.subitem.id, 'picture_ids', [
                data.imageFile.id
              ]);
              newItem.subitem.pictures = [data.imageFile];
            } catch (e) {
              console.warn('initial file persist failed', e);
            }
          }

          // Use a fresh state snapshot to avoid race conditions when adding multiple items quickly
          set(current => {
            if (!current.sequence) return current;
            const authoritativePosition = data.data?.position;
            if (
              newItem?.subitem?.position &&
              authoritativePosition &&
              newItem.subitem.position < authoritativePosition
            ) {
              newItem.subitem.position = authoritativePosition;
            }

            const created = { ...data.newItem, id: newItem.subitem.id };

            function attachToParent(items: any[]): any[] {
              return items?.map(it => {
                if (it.id == data.data.parent_id) {
                  const childItems = [...(it.items || []), created];
                  // ensure ordering when parent-specific (we rely on explicit position inside each child if present)
                  childItems.sort(
                    (a, b) => (a.position || 0) - (b.position || 0)
                  );
                  return { ...it, items: childItems };
                }
                return it.items
                  ? { ...it, items: attachToParent(it.items) }
                  : it;
              });
            }

            let allItems = attachToParent(current.sequence.items);

            if (!data.data.parent_id) {
              const merged = {
                ...newItem.subitem,
                ...data.newItem,
                id: newItem.subitem.id,
                position: newItem.subitem.position
              };
              allItems = insertAndReorder(allItems, merged);
            }

            const updatedSequence = { ...current.sequence, items: allItems };
            const updatedTraining = {
              ...current.training,
              chapters: current.training.chapters.map((chapter: any) => ({
                ...chapter,
                chapterSequences: chapter.chapterSequences.map((seq: any) =>
                  seq.sequence.id === current.sequence.id
                    ? { ...seq, sequence: updatedSequence }
                    : seq
                )
              }))
            };

            // Fire optional creation callback after state commit
            if (typeof data.onCreated === 'function') {
              try {
                data.onCreated(newItem.subitem.id);
              } catch (e) {
                /* ignore */
              }
            }

            return {
              ...current,
              sequence: updatedSequence,
              training: updatedTraining
            };
          });
        })
        .catch(e => console.warn('addSubItem failed', e));

      return { ...state };
    }),
  removeSubItem: (subItemId: number | string) =>
    set(state => {
      if (!state.apiService) {
        console.error('ApiService is not initialized.');
        return state;
      }

      const _history = state.history.filter(
        item => item.elementId != subItemId
      );

      const activeItem = state.history.find(
        item => item.elementId == subItemId && item.active === true
      );

      if (activeItem) {
        const activeIndex = state.history.findIndex(
          item => item.elementId == subItemId && item.active === true
        );

        for (let i = activeIndex - 1; i >= 0; i--) {
          const previousItem = state.history[i];
          if (previousItem.elementId != subItemId) {
            const itemToActivate = _history.find(
              item =>
                item.elementId == previousItem.elementId &&
                item.timestamp === previousItem.timestamp
            );
            if (itemToActivate) {
              itemToActivate.active = true;
            }
            break;
          }
        }
      }

      saveHistoryToStorage(_history);
      set({ history: _history });

      state.apiService.removeSubItem(subItemId).then(response => {
        extractAndStoreMetadata(response, state);
      });

      const removeItemById = (items: any[], id: number | string): any[] => {
        const filteredItems = items
          .filter(item => item.id != id)
          .map(item => {
            return item.items
              ? { ...item, items: removeItemById(item.items, id) }
              : item;
          });

        const sortedItems = filteredItems.sort(
          (a, b) => a.position - b.position
        );

        let gapIndex = -1;
        for (let i = 0; i < sortedItems.length; i++) {
          if (sortedItems[i].position !== i + 1) {
            gapIndex = i;
            break;
          }
        }

        if (gapIndex === -1) {
          return sortedItems;
        }

        return sortedItems.map((item, index) => ({
          ...item,
          position: index + 1
        }));
      };

      const updatedSequence = {
        ...state.sequence,
        items: removeItemById(state.sequence.items, subItemId)
      };

      const updatedTraining = {
        ...state.training,
        chapters: state.training.chapters.map((chapter: any) => ({
          ...chapter,
          chapterSequences: chapter.chapterSequences.map((seq: any) => {
            return seq.sequence.id === state.sequence.id
              ? { ...seq, sequence: updatedSequence }
              : seq;
          })
        }))
      };

      return {
        ...state,
        sequence: updatedSequence,
        training: updatedTraining,
        history: _history
      };
    }),
  updateSubItem: async (
    subItemId: number | string,
    fieldType: string,
    value: any
  ) => {
    const state = get();

    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return { error: true, message: 'API service not available' };
    }

    try {
      const response = await state.apiService.updateSubItem(
        subItemId,
        fieldType,
        value
      );

      extractAndStoreMetadata(response, state);

      if (!response.error) {
        if (
          fieldType === 'item_bots_attributes' &&
          value[0]?.conversation_bot_id
        ) {
          state.updateSequence(
            subItemId,
            'conversationBotId',
            value[0].conversation_bot_id
          );
        } else {
          state.updateSequence(subItemId, fieldType, value);
        }

        return { error: false };
      }

      return response;
    } catch (error) {
      console.error('Error updating subitem:', error);
      return { error: true, message: 'Failed to update subitem' };
    }
  },
  updateFile: (id: string, data: any, type: string, parentId?: string) => {
    // make an optimistic update then persist and re-fetch authoritative sequence
    const state = get();
    // ensure field is always a string (avoid boolean/union inference)
    let field = 'video_ids';
    let normalizedType = type;
    if (type === 'documents') {
      field = 'document_ids';
    } else if (type === 'pictures' || type === 'PHONE' || type == 'CARD') {
      field = 'picture_ids';
      normalizedType = 'pictures';
    } else if (type === 'audios') {
      field = 'audio_ids';
    }

    if (type === 'pictures' && data && data.id) {
      const collectBackgroundPictures = (items: any[]): string[] => {
        const result: string[] = [];
        items.forEach(it => {
          if (it.type === 'BACKGROUND' && it.pictures) {
            it.pictures.forEach((p: any) => {
              if (p && p.id) result.push(String(p.id));
            });
          }
          if (it.items) result.push(...collectBackgroundPictures(it.items));
        });
        return result;
      };
      const allBgPictureIds = collectBackgroundPictures(state.sequence.items);
      const newId = String(data.id);
      const replacingSame = String(id) === newId;
      if (!replacingSame && allBgPictureIds.includes(newId)) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'You are unable to have duplicate background pictures.'
        });
        return;
      }
    }

    const findParentElement = (
      items: any[],
      targetId: string,
      ttype: string
    ): any | undefined => {
      if (parentId) {
        const parentItem = items.find(item => item.id == parentId);
        if (parentItem) {
          return parentItem.id;
        }
      }

      for (const item of items) {
        if (
          item[ttype] &&
          item[ttype].some((subItem: any) => subItem.id == targetId)
        ) {
          return item.id;
        }
        if (item.items) {
          const found = findParentElement(item.items, targetId, ttype);
          if (found) return found;
        }
      }
      return undefined;
    };

    const getBackgroundId = (
      items: any[],
      fileId: string
    ): string | undefined => {
      for (const item of items) {
        if (item.type === 'BACKGROUND' && item[normalizedType]) {
          const hasFile = item[normalizedType].some(
            (file: any) => file.id === fileId
          );
          if (hasFile) return item.id;
        }
      }
      return undefined;
    };

    let backgroundId = getBackgroundId(state.sequence.items, id);
    const parentElementId = findParentElement(
      state.sequence.items,
      id,
      normalizedType
    );

    // Check if the file being updated belongs to a background item
    const isUpdatingBackgroundFile = (
      items: any[],
      fileId: string
    ): boolean => {
      for (const item of items) {
        if (item.type === 'BACKGROUND' && item[normalizedType]) {
          const hasFile = item[normalizedType].some(
            (file: any) => file.id === fileId
          );
          if (hasFile) return true;
        }
      }
      return false;
    };

    const isTargetBackground = isUpdatingBackgroundFile(
      state.sequence.items || [],
      id
    );

    const _history = state.history;

    _history.forEach(item => {
      if (item.sequenceId == state.sequence.id) {
        item.active = false;
      }
    });

    if (parentElementId && parentElementId != backgroundId) {
      backgroundId = undefined;
    }

    const GetFiles = (items: any[], targetBackgroundId: string): string[] => {
      const targetBackground = items.find(
        item => item.id === targetBackgroundId
      );
      if (targetBackground && targetBackground[normalizedType]) {
        return targetBackground[normalizedType].map((file: any) => file.id);
      }
      return [];
    };

    const updateFiles = (items: any[]): any[] => {
      return items.map(item => {
        if (item.type === 'BACKGROUND' && item[normalizedType]) {
          const existingHistoryItem = _history.find(
            historyItem =>
              historyItem.sequenceId == state.sequence.id &&
              historyItem.elementId == item.id &&
              historyItem.fieldType == normalizedType &&
              historyItem.value &&
              Array.isArray(historyItem.value) &&
              historyItem.value.some((file: any) => file.id == data.id)
          );

          if (
            !_history.find(
              historyItem => historyItem.sequenceId == state.sequence.id
            )
          ) {
            _history.push({
              sequenceId: state.sequence.id,
              elementId: undefined,
              fieldType: undefined,
              value: undefined,
              oldValue: undefined,
              type: undefined,
              active: false,
              timestamp: Date.now()
            });
          }

          if (!existingHistoryItem) {
            _history.push({
              sequenceId: state.sequence.id,
              elementId: item.id,
              fieldType: normalizedType,
              value: item[normalizedType].map((file: any) => {
                return file.id == id ? data : file;
              }),
              oldValue: item[normalizedType],
              type: normalizedType,
              active: true,
              timestamp: Date.now()
            });
          }

          return {
            ...item,
            [normalizedType]: item[normalizedType].map((file: any) =>
              file.id == id ? data : file
            )
          };
        }
        return item.items ? { ...item, items: updateFiles(item.items) } : item;
      });
    };

    const updateNonBackgroundFiles = (items: any[]): any[] => {
      return items.map(item => {
        if (item.id === parentElementId || item.id === id) {
          const existingHistoryItem = _history.find(
            historyItem =>
              historyItem.sequenceId == state.sequence.id &&
              historyItem.elementId == item.id &&
              historyItem.fieldType == normalizedType &&
              historyItem.value &&
              historyItem.value.length > 0 &&
              historyItem.value[0].id == data.id
          );

          if (
            !_history.find(
              historyItem => historyItem.sequenceId == state.sequence.id
            )
          ) {
            _history.push({
              sequenceId: state.sequence.id,
              elementId: undefined,
              fieldType: undefined,
              value: undefined,
              oldValue: undefined,
              type: undefined,
              active: false,
              timestamp: Date.now()
            });
          }

          if (!existingHistoryItem) {
            _history.push({
              sequenceId: state.sequence.id,
              elementId: item.id,
              fieldType: normalizedType,
              value: [data],
              oldValue: item[normalizedType] || [],
              type: normalizedType,
              active: true,
              timestamp: Date.now()
            });
          }

          return { ...item, [normalizedType]: [data] };
        }
        return item.items
          ? { ...item, items: updateNonBackgroundFiles(item.items) }
          : item;
      });
    };

    const useBackgroundUpdate = backgroundId && isTargetBackground;
    const updatedSequence = {
      ...state.sequence,
      items: useBackgroundUpdate
        ? updateFiles(state.sequence.items)
        : updateNonBackgroundFiles(state.sequence.items)
    };

    const updatedTraining = {
      ...state.training,
      chapters: state.training.chapters.map((chapter: any) => ({
        ...chapter,
        chapterSequences: chapter.chapterSequences.map((seq: any) => {
          if (seq.sequence.id === state.sequence.id) {
            return { ...seq, sequence: updatedSequence };
          }
          return seq;
        })
      }))
    };

    // optimistic set
    // id = parseInt(id)
    set({ ...state, sequence: updatedSequence, training: updatedTraining });

    // persist and re-fetch authoritative sequence
    (async () => {
      try {
        if (!state.apiService) return;

        if (useBackgroundUpdate && backgroundId) {
          await state.apiService.updateSubItem(
            backgroundId,
            field,
            GetFiles(updatedSequence.items, backgroundId)
          );
        } else {
          const elId = parentElementId || id;
          if (elId) {
            await state.apiService.updateSubItem(elId, field, [data.id]);
          }
        }

        const seq = await state.apiService.getSequenceInfo(state.sequence.id);
        if (seq && seq.sequence) {
          extractAndStoreMetadata(seq, state);

          // replace sequence and update training copy
          const fresh = seq.sequence;
          const freshTraining = {
            ...get().training,
            chapters: get().training.chapters.map((chapter: any) => ({
              ...chapter,
              chapterSequences: chapter.chapterSequences.map((s: any) =>
                s.sequence.id === fresh.id ? { ...s, sequence: fresh } : s
              )
            }))
          };
          saveHistoryToStorage(_history);
          set({
            ...get(),
            sequence: fresh,
            training: freshTraining,
            history: _history
          });
        }
      } catch (e) {
        // ignore persistence errors for now
        console.error('updateFile persistence error', e);
      }
    })();
  },
  setNarration: (audio: any) =>
    set(state => {
      if (!state.apiService) {
        console.error('ApiService is not initialized.');
        return state;
      }

      state.apiService
        .setNarration(state.sequence.id, audio.id)
        .then(updatedSequence => {
          if (updatedSequence && updatedSequence.sequence) {
            const updatedTraining = {
              ...state.training,
              chapters: state.training.chapters.map((chapter: any) => ({
                ...chapter,
                chapterSequences: chapter.chapterSequences.map((seq: any) => {
                  return seq.sequence.id === updatedSequence.sequence.id
                    ? { ...seq, sequence: updatedSequence.sequence }
                    : seq;
                })
              }))
            };

            set({
              ...state,
              sequence: updatedSequence.sequence,
              training: updatedTraining
            });
          }
        });

      return { ...state };
    }),

  createNewChapter: (chapterName: string) =>
    set(state => {
      if (!state.apiService) {
        console.error('ApiService is not initialized.');
        return state;
      }

      if (!chapterName) {
        console.error('Data is not valid.');
        return state;
      }

      const maxPosition = state.training.chapters.reduce(
        (max: number, chapter: any) => {
          return chapter.position > max ? chapter.position : max;
        },
        0
      );

      const data = {
        title: chapterName,
        training_id: state.training.id,
        duration: 0,
        position: maxPosition + 1
      };

      state.apiService.createNewChapter(data).then(newChapter => {
        state.training.chapters.push(newChapter.chapter);
        state.lastChapterId = newChapter.chapter.id;
        set({
          ...state,
          training: state.training,
          lastChapterId: state.lastChapterId
        });
      });

      return { ...state };
    }),

  removeHistoryForSequence: (sequenceId: number) =>
    set(state => {
      const _history = state.history.filter(
        item => item.sequenceId != sequenceId
      );

      saveHistoryToStorage(_history);
      set({ history: _history });
      return { ...state };
    }),

  updateChapter: (chapterId: number, data: any) =>
    set(state => {
      if (!state.apiService) {
        console.error('ApiService is not initialized.');
        return state;
      }

      state.apiService.updateChapter(chapterId, data);

      return { ...state };
    }),

  setAIModificationStatus: (status: Partial<AIModificationStatus>) =>
    set(state => ({
      ...state,
      aiModification: {
        ...state.aiModification,
        ...status
      }
    })),

  resetAIModificationStatus: () =>
    set(state => ({
      ...state,
      aiModification: {
        ...state.aiModification,
        isLoading: false,
        error: null,
        success: false
      }
    })),

  modifySequenceWithAI: async (instructions: string) => {
    const state = get();
    const { setAIModificationStatus } = get();

    if (!state.apiService) {
      const error = 'ApiService is not initialized.';
      setAIModificationStatus({ isLoading: false, error, success: false });
      return { error: true, message: error };
    }
    if (!state.sequence?.id) {
      const error = 'No current sequence found.';
      setAIModificationStatus({ isLoading: false, error, success: false });
      return { error: true, message: error };
    }

    const trimmedInstructions = instructions.trim();

    if (!trimmedInstructions) {
      const error = 'Instructions cannot be empty.';
      setAIModificationStatus({ isLoading: false, error, success: false });
      return { error: true, message: error };
    }

    setAIModificationStatus({
      isLoading: true,
      error: null,
      success: false,
      lastPrompt: instructions
    });

    try {
      const startResponse = await state.apiService.startAIModification(
        state.sequence.id,
        trimmedInstructions
      );

      if (startResponse.error) {
        setAIModificationStatus({
          isLoading: false,
          error: startResponse.message || 'Failed to start AI modification',
          success: false
        });
        return startResponse;
      }

      const { ai_response_id } = startResponse;
      if (!ai_response_id) {
        const error = 'No AI response ID received';
        setAIModificationStatus({ isLoading: false, error, success: false });
        return { error: true, message: error };
      }

      return new Promise(resolve => {
        const pollingInterval = 2000; // 2 seconds
        const maxAttempts = 150; // 5 minutes max
        let attempts = 0;

        const pollInterval = setInterval(async () => {
          attempts++;

          try {
            const currentState = get();
            if (!currentState.apiService || !currentState.sequence?.id) {
              clearInterval(pollInterval);
              const error = 'Session lost';
              setAIModificationStatus({
                isLoading: false,
                error,
                success: false
              });
              resolve({ error: true, message: error });
              return;
            }

            const statusResponse =
              await currentState.apiService.pollAIModificationStatus(
                currentState.sequence.id,
                ai_response_id
              );

            if (statusResponse.error) {
              clearInterval(pollInterval);
              setAIModificationStatus({
                isLoading: false,
                error: statusResponse.message || 'Error checking status',
                success: false
              });
              resolve(statusResponse);
              return;
            }
            const { status, error } = statusResponse;

            if (status === 'completed') {
              clearInterval(pollInterval);

              try {
                const sequenceResponse =
                  await currentState.apiService.getSequenceInfo(
                    currentState.sequence.id
                  );

                extractAndStoreMetadata(sequenceResponse, currentState);

                if (sequenceResponse.sequence) {
                  const sequence = sequenceResponse.sequence;
                  const updatedTraining = {
                    ...currentState.training,
                    chapters: currentState.training.chapters.map(
                      (chapter: any) => ({
                        ...chapter,
                        chapterSequences: chapter.chapterSequences.map(
                          (seq: any) =>
                            seq.sequence.id === currentState.sequence.id
                              ? { ...seq, sequence }
                              : seq
                        )
                      })
                    )
                  };

                  const filteredHistory = currentState.history.filter(
                    item => item.sequenceId !== currentState.sequence.id
                  );
                  saveHistoryToStorage(filteredHistory);

                  set({
                    sequence,
                    training: updatedTraining,
                    history: filteredHistory,
                    aiModification: {
                      isLoading: false,
                      error: null,
                      success: true,
                      lastPrompt: currentState.aiModification.lastPrompt
                    }
                  });

                  resolve({ success: true, sequence });
                } else {
                  const error = 'Failed to fetch updated sequence';
                  setAIModificationStatus({
                    isLoading: false,
                    error,
                    success: false
                  });
                  resolve({ error: true, message: error });
                }
              } catch (fetchError: any) {
                const error =
                  fetchError.message || 'Failed to fetch updated sequence';
                setAIModificationStatus({
                  isLoading: false,
                  error,
                  success: false
                });
                resolve({ error: true, message: error });
              }
              return;
            }

            if (status === 'failed') {
              clearInterval(pollInterval);
              const errorMsg = error || 'AI modification failed';
              setAIModificationStatus({
                isLoading: false,
                error: errorMsg,
                success: false
              });
              resolve({ error: true, message: errorMsg });
              return;
            }

            if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              const timeoutError = 'AI modification timed out';
              setAIModificationStatus({
                isLoading: false,
                error: timeoutError,
                success: false
              });
              resolve({ error: true, message: timeoutError });
              return;
            }
          } catch (pollError: any) {
            clearInterval(pollInterval);
            const error = pollError.message || 'Network error during polling';
            setAIModificationStatus({
              isLoading: false,
              error,
              success: false
            });
            resolve({ error: true, message: error });
          }
        }, pollingInterval);
      });
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to start AI modification';
      setAIModificationStatus({
        isLoading: false,
        error: errorMsg,
        success: false
      });
      return { error: true, message: errorMsg };
    }
  },
  updateTrainingTitle: (title: string) => {
    const state = get();

    state.apiService?.updateTrainingTitle(state.training.id, title);
    set(state => ({ ...state, training: { ...state.training, title } }));
  }
}));

export const getEditorState = () => useEditorStore.getState();

interface EditorProviderProps {
  children: ReactNode;
  contentEditable?: boolean;
}

export const EditorProvider: FC<EditorProviderProps> = ({
  children,
  contentEditable = false
}) => {
  const setContentEditable = useEditorStore(state => state.setContentEditable);

  React.useEffect(() => {
    setContentEditable(contentEditable);
  }, [contentEditable, setContentEditable]);

  return <>{children}</>;
};
